import {decodeOrThrow, readonlyType} from '@raison/io-ts-tools';
import {MobilePhone, NonEmptyString, Password} from '@raison/types';
import {isLeft} from 'fp-ts/Either';
import {boolean, string} from 'io-ts';
import {handleAuthDataReceived, login, LoginRequestData} from '~/entities/user';
import {sendPersonalizedErrorLogs} from '~/shared/api/legacy_services/logs';
import {sentryFeedBack} from '~/shared/api/legacy_services/sentry';
import {appLocalStorage, appSessionStorage} from '~/shared/persistence';
import {getBiometryHelper} from '~/shared/utils/biometryHelper';
import {hashPassword} from '~/shared/utils/hashPassword';
import {isMobileApp} from '~/shared/utils/mobileDeviceDetect';
import {getSavePassHandler} from '~/shared/utils/savePass';

const biometryHelper = getBiometryHelper();
const savePassHandler = getSavePassHandler();

interface GoNativeResponseForStatus {
    hasTouchId: boolean;
    biometryType: 'touchId' | 'faceId' | 'none';
    hasSecret: boolean;
}

interface GoNativeResponseForGet {
    success: boolean;
    error:
        | undefined
        | 'duplicateItem'
        | 'itemNotFound'
        | 'authenticationFailed'
        | 'genericError'
        | 'userCanceled'
        | 'unimplemented';
    secret?: string;
}

interface Secret {
    username: string;
    password: string;
}
namespace Secret {
    const codec = readonlyType({username: string, password: string});
    const BASE64_PREFIX = 'data:application/json,base64,';
    export function decode(string: string): Secret {
        if (string.startsWith(BASE64_PREFIX)) {
            string = string.slice(BASE64_PREFIX.length);
            string = window.atob(string);
        }
        const secret = decodeOrThrow(codec, JSON.parse(string));
        return secret;
    }

    export function encode({username, password}: Secret): string {
        let string = '';
        string = JSON.stringify({username, password});
        string = window.btoa(string);
        string = BASE64_PREFIX + string;
        return string;
    }
}

export function initBiometry() {
    Object.assign(window, {
        gonative_status_afterlogin,
        gonative_status_beforelogin,
        gonative_secret_callback,
        gonative_status_afterfail,
        gonative_delete_secret_callback,
        biometry_type_callback,
    });

    // the user may unlock biometry or change its permission while the app is in the background
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') get_touch_id_availability();
    });
}

function gonative_status_afterlogin(data: GoNativeResponseForStatus) {
    // refresh the cache on every status response, otherwise `hasTouchId: false` after a lockout sticks
    biometry_type_callback(data);
    const username = appLocalStorage.username.get()!;
    const password = savePassHandler.getPassword();
    savePassHandler.removePassword();

    if (data?.hasTouchId) {
        // biometry is activated only once the saved secret is read back with a successful scan,
        // see `gonative_secret_callback`
        appLocalStorage.saveSecretError.remove();
        window.location.href = `gonative://auth/save?secret=${Secret.encode({username, password})}`;
    } else {
        // Error occurs when touchId is not available
        appLocalStorage.saveSecretError.set('true');
    }
}

/**
 * This is dumb and bad hack but no different that all the global variables in localStorage and various "helper" classes.
 * It is here to prevent simultaneous activations of biometry
 *
 * Word "ceremony" comes from https://w3c.github.io/webauthn/#sctn-terminology
 * Here it just means "a process for authenticating with biometry", not calling it `biometryProcessIsInProgress` because
 * "ceremony" sounds cooler and I wan't to ditch gonative
 */
let biometryCeremonyIsInProgress = false;
function gonative_status_beforelogin(data: GoNativeResponseForStatus) {
    biometry_type_callback(data);
    if (biometryCeremonyIsInProgress) {
        return;
    }

    if (data?.hasTouchId && data.hasSecret) {
        biometryCeremonyIsInProgress = true;
        // remove previous failed attempt
        appLocalStorage.dataBiometry.remove();
        // Prompt the user to use the fingerprint to log in
        window.location.href = `gonative://auth/get?${new URLSearchParams({
            callbackFunction: 'gonative_secret_callback',
            callbackOnCancel: '1',
        })}`;
    } else {
        deActivateBiometry();
        biometry_type_callback({hasTouchId: false, biometryType: 'none', hasSecret: false});
        gonative_delete_secret();
        const callPointBiometry = appLocalStorage.callPointBiometry.get();
        if (callPointBiometry === 'loginForm') window.restartAppAt('/login');
        else if (callPointBiometry === 'signupForm') window.restartAppAt('/signup');
        else if (callPointBiometry === 'forgotPasswordForm') window.restartAppAt('/forgot-password');
        else if (callPointBiometry === 'settings') window.restartAppAt('/settings');
        sendPersonalizedErrorLogs({
            level: 'info',
            message: `Biometrics is not activated; ${JSON.stringify(data)}`,
            tags: ['Biometry', 'signIn'],
        });
    }
}

/**
 * Called after a failed biometry ceremony. During a lockout the system reports biometry as unavailable,
 * after a regular fail it is still available and the user can try again
 */
function gonative_status_afterfail(data: GoNativeResponseForStatus) {
    if (data?.hasTouchId) {
        biometry_type_callback(data);
        biometryHelper.runOnBiometryFailed('failed');
        return;
    }
    // the user has to activate biometry in the app again once the lockout is gone;
    // reset goes before the cache refresh, so status subscribers already see biometry deactivated
    deActivateBiometry();
    gonative_delete_secret();
    appLocalStorage.runBiometryActivation.remove();
    biometry_type_callback(data);
    biometryHelper.runOnBiometryFailed('lockout');
}

/**
 * Drops an activation that was not confirmed with a successful scan, e.g. when the user leaves
 * the activation screen after a regular fail. The secret is saved before the scan, so it has to be removed
 */
export function cancelPendingBiometryActivation() {
    // the flag is removed once the scan succeeds or a lockout resets biometry
    const activationIsPending = appLocalStorage.runBiometryActivation.get() === 'start';
    if (!activationIsPending) return;
    appLocalStorage.runBiometryActivation.remove();
    biometryHelper.setRunAuth(true);
    deActivateBiometry();
    gonative_delete_secret();
}

export function gonative_auth() {
    window.location.href = 'gonative://auth/status?callbackFunction=gonative_status_beforelogin';
}

export async function gonative_auth_async() {
    gonative_auth();

    return new Promise<undefined>((resolve, reject) =>
        setTimeout(() => {
            const biometryData = appLocalStorage.dataBiometry.get();
            if (biometryData?.includes('authenticationFailed')) {
                return reject();
            } else {
                return resolve(undefined);
            }
        }, 500)
    );
}

function gonative_delete_secret_callback() {
    // noop for legacy reasons; used to have logic that is no longer needed, the function
    // itself is kept just in case callbackFunction param is expected in gonative://auth/delete
    // it might be optional, don't have time to check right now
}

export function gonative_delete_secret() {
    window.location.href = `gonative://auth/delete?callbackFunction=gonative_delete_secret_callback`;
}

export function gonative_save_secret() {
    window.location.href = `gonative://auth/status?callbackFunction=gonative_status_afterlogin`;
}

function biometry_type_callback({hasTouchId, biometryType, hasSecret}: GoNativeResponseForStatus) {
    appLocalStorage.biometryType.set(biometryType);
    appLocalStorage.hasTouchId.set(hasTouchId);
    appLocalStorage.hasSecret.set(hasSecret);
    biometryHelper.runOnBiometryStatus();
}

export function get_touch_id_availability() {
    if (isMobileApp()) window.location.href = 'gonative://auth/status?callbackFunction=biometry_type_callback';
}

export function get_biometry_type() {
    //returns: faceId, touchId, none, null
    return appLocalStorage.biometryType.get();
}

export const activateBiometry = () => {
    appLocalStorage.biometryStatus.set('activated');
    appLocalStorage.timestampBiometryActivation.set(`${+new Date()}`);
};

/**
 * Just sets `biometryStatus` in local storage to `"not activated"`
 */
export const deActivateBiometry = () => {
    appLocalStorage.biometryStatus.set('not activated');
};

export const isBiometryActivated = () => {
    const biometryStatus = appLocalStorage.biometryStatus.get();
    const hasTouchId = appLocalStorage.hasTouchId.get();

    if (isMobileApp() && hasTouchId && biometryStatus === 'activated') return true;
    return false;
};

/**
 * Executes login via biometry ceremony
 */
async function gonative_secret_callback(data: GoNativeResponseForGet) {
    try {
        if (data && !data.success && data.error === 'userCanceled') {
            return;
        }

        if (!data || !data.success || !data.secret) {
            appLocalStorage.callPointBiometry.remove();
            appLocalStorage.dataBiometry.set(JSON.stringify(data));
            sendPersonalizedErrorLogs({
                level: 'info',
                message: `Not valid biometrics; ${JSON.stringify(data)}`,
                tags: ['Biometry', 'signIn'],
            });
            // error codes don't tell a lockout from a regular fail, the fresh status does
            window.location.href = 'gonative://auth/status?callbackFunction=gonative_status_afterfail';
            return;
        }

        biometryHelper.runLoaderLoginBiometry(true);
        {
            const usernameLocalStorage = appLocalStorage.username.get();
            const credentials = Secret.decode(data.secret);
            const usernameSecretSaved = credentials.username;

            const normalizedUsernameLocalStorage = usernameLocalStorage
                ? normalizePhoneNumber(usernameLocalStorage)
                : null;
            const normalizedUsernameSecretSaved = normalizePhoneNumber(usernameSecretSaved);

            if (normalizedUsernameLocalStorage !== normalizedUsernameSecretSaved) {
                const err = {
                    type: 'biometry',
                    code: 401,
                    message: 'Usernames are not equal',
                };
                sentryFeedBack('Biometry', 'Usernames are not equal', {
                    normalizedUsernameLocalStorage,
                    normalizedUsernameSecretSaved,
                });
                appLocalStorage.authError.set(JSON.stringify(err));
                window.restartAppAt('/login');
                return;
            }

            if (!biometryHelper.runAuth) {
                biometryHelper.setRunAuth(true);
                activateBiometry();

                if (appLocalStorage.runBiometryActivation.get() === 'start') {
                    appLocalStorage.runBiometryActivation.remove();
                }
                biometryHelper.runLoaderLoginBiometry(false);
                // only the screen that started the ceremony should react, otherwise a callback
                // left by an unmounted screen closes the dialog of the current one
                if (appLocalStorage.callPointBiometry.get() === 'settings') {
                    biometryHelper.runActBiometryModalSettings();
                } else {
                    biometryHelper.runActBiometryModalLogin();
                }
                return;
            }

            const username = decodeOrThrow(MobilePhone, normalizedUsernameSecretSaved);
            const password = decodeOrThrow(Password, credentials.password);
            const onesignal_id = window.oneSignalInfo?.oneSignalUserId;
            const onesignal_consent = window.oneSignalInfo?.oneSignalSubscribed;
            const loginData = {
                username,
                password,
                onesignal_id:
                    typeof onesignal_id === 'undefined' ? undefined : decodeOrThrow(NonEmptyString, onesignal_id),
                onesignal_consent:
                    typeof onesignal_consent === 'undefined' ? undefined : decodeOrThrow(boolean, onesignal_consent),
            };

            await signInAsync(loginData);
        }
        biometryHelper.runLoaderLoginBiometry(false);

        const authErr = appLocalStorage.authError.get();
        const callPointBiometry = appLocalStorage.callPointBiometry.get();
        appLocalStorage.callPointBiometry.remove();

        if (
            !authErr &&
            (callPointBiometry === 'loginForm' ||
                callPointBiometry === 'signupForm' ||
                callPointBiometry === 'forgotPasswordForm')
        ) {
            window.restartAppAt('/');
        } else if (!authErr && callPointBiometry === 'settings') window.restartAppAt('/settings');
    } finally {
        biometryCeremonyIsInProgress = false;
    }
}

let evil_dispatch: AppDispatch;
export function setDispatch(dispatch: AppDispatch) {
    evil_dispatch = dispatch;
}

async function signInAsync(data: LoginRequestData) {
    try {
        const response = await login(data);
        if (isLeft(response)) {
            if (response.left.type === 'ApiClientError') {
                throw response.left.error;
            } else {
                throw {};
            }
        }
        const result = response.right;
        const hPass = hashPassword(data.password, result.user.username);

        evil_dispatch(handleAuthDataReceived(result));

        if (hPass) {
            appSessionStorage.hPass.set(hPass);
        }
    } catch (err) {
        sendPersonalizedErrorLogs({
            level: 'info',
            message: `Biometry sign in query error; ${JSON.stringify(err)}`,
            tags: ['Biometry', 'signIn'],
        });
        appLocalStorage.authError.set(JSON.stringify(err));
        window.restartAppAt('/login');
    } finally {
        biometryHelper.runLoaderLoginBiometry(false);
    }
}

function normalizePhoneNumber(phoneStr: string) {
    if (!phoneStr) return phoneStr;
    let newValue = '';

    if (phoneStr !== '')
        if (phoneStr.charAt(0) === '+') {
            newValue = phoneStr.slice(1);
        }

    return `+${newValue.replace(/\D/g, '')}`;
}
